import { useCallback, useEffect, useRef, useState } from "react"

import { Button } from "@atoms/index"

import AlertBanner from "@molecules/AlertBanner/AlertBanner"
import CheckboxCard from "@molecules/CheckboxCard/CheckboxCard"
import Dialog from "@molecules/Dialog/Dialog"

import styles from "./EstimateQuickOrderDialog.module.scss"
import { EstimateQuickOrderDialogProps } from "./EstimateQuickOrderDialog.types"

export default function EstimateQuickOrderDialog(props: EstimateQuickOrderDialogProps) {
    const { isOpen, onOpenChange, lineItems, onCreateOrder } = props

    const freeLineItems = lineItems.filter((lineItem) => !lineItem.purchase_order)
    const freeLineItemIDs = freeLineItems.map((lineItem) => lineItem.id)
    const allOrdered = freeLineItemIDs.length === 0

    // A purchase order has a single vendor, so the default selection can only
    // pre-select every free line when those lines share at most one vendor.
    // When the free lines span multiple vendors, start empty and let the user
    // pick a compatible set.
    const distinctFreeVendors = new Set(
        freeLineItems.map((lineItem) => lineItem.ecommerce_vendor).filter((vendor): vendor is string => !!vendor),
    )
    const defaultSelectedLineItemIDs = distinctFreeVendors.size > 1 ? [] : freeLineItemIDs

    const [selectedLineItemIDs, setSelectedLineItemIDs] = useState(defaultSelectedLineItemIDs)
    const [hasSelectionError, setHasSelectionError] = useState(false)

    // Store onOpenChange in a ref so the callback identity stays stable
    // across renders. The Popover's useEffect depends on onOpenChange,
    // and an unstable reference causes an infinite re-render loop.
    const onOpenChangeRef = useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange

    const stableOnOpenChange = useCallback((isNowOpen: boolean) => {
        onOpenChangeRef.current(isNowOpen)
    }, [])

    // Re-sync the selection each time the dialog opens so lines ordered (or
    // freed) since the last open are reflected.
    useEffect(() => {
        if (isOpen) {
            setSelectedLineItemIDs(defaultSelectedLineItemIDs)
            setHasSelectionError(false)
        }
    }, [isOpen])

    const handleToggle = (lineItemID: number) => {
        setSelectedLineItemIDs((prevSelected) =>
            prevSelected.includes(lineItemID)
                ? prevSelected.filter((selectedID) => selectedID !== lineItemID)
                : [...prevSelected, lineItemID],
        )
    }

    // Derive the vendor a purchase order would be created for from the current
    // selection: the first selected line that carries a vendor. While an active
    // vendor exists, lines belonging to a different vendor are disabled so the
    // selection can never span more than one vendor.
    const activeVendor =
        lineItems.find((lineItem) => selectedLineItemIDs.includes(lineItem.id) && lineItem.ecommerce_vendor)
            ?.ecommerce_vendor ?? null

    const handleCreateOrder = () => {
        if (selectedLineItemIDs.length === 0) {
            setHasSelectionError(true)
        } else {
            onCreateOrder(selectedLineItemIDs)
        }
    }

    return (
        <Dialog.Root mode="dialog" variant="default" isOpen={isOpen} onOpenChange={stableOnOpenChange}>
            <Dialog.Content>
                <Dialog.Header.Root>
                    <Dialog.Header.Title title="Create Purchase Order" />
                    <Dialog.Header.CloseButton />
                </Dialog.Header.Root>
                <Dialog.Body>
                    <div className={styles.body}>
                        {allOrdered ? (
                            <AlertBanner
                                type="card"
                                status="info"
                                message="All of this estimate's part line items are already on a purchase order."
                            />
                        ) : (
                            <p>Select which line items you&apos;d like to order:</p>
                        )}
                        {lineItems.map((lineItem) => {
                            const orderedOn = lineItem.purchase_order
                            const hasVendorConflict =
                                !!activeVendor &&
                                !!lineItem.ecommerce_vendor &&
                                lineItem.ecommerce_vendor !== activeVendor

                            let supportText = ""
                            if (orderedOn) {
                                supportText = `On ${orderedOn.label}`
                            } else if (lineItem.ecommerce_vendor) {
                                supportText = lineItem.ecommerce_vendor_display_name ?? ""
                            }

                            return (
                                <CheckboxCard
                                    key={lineItem.id}
                                    checked={selectedLineItemIDs.includes(lineItem.id)}
                                    onCheckedChange={() => handleToggle(lineItem.id)}
                                    label={`${lineItem.description} (Qty: ${parseFloat(lineItem.quantity)})`}
                                    showLabel={true}
                                    supportText={supportText}
                                    showSupportText={!!supportText}
                                    isControlled={false}
                                    isDisabled={!!orderedOn || hasVendorConflict}
                                />
                            )
                        })}
                        {hasSelectionError && (
                            <span className={styles.errorMessage}>Please select at least one line item to order.</span>
                        )}
                    </div>
                </Dialog.Body>
                <Dialog.Footer.Root>
                    <Button
                        variant="solid"
                        size="lg"
                        onClick={handleCreateOrder}
                        isFullWidth={true}
                        isDisabled={allOrdered}
                    >
                        Create Order
                    </Button>
                </Dialog.Footer.Root>
            </Dialog.Content>
        </Dialog.Root>
    )
}
